import React, { useEffect, useState, useCallback } from "react";
import Layout from "@/components/Layout";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Users, Plus, KeyRound, RotateCw, ShieldCheck, Power, Copy, Check, Trash2, Webhook as WebhookIcon, AlertOctagon, FileDown,
} from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" }); }
  catch { return iso; }
}

function CopyBox({ label, value, testId }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      console.error(e);
    }
  };
  return (
    <div className="space-y-1" data-testid={testId}>
      <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{label}</div>
      <div className="flex items-center gap-2">
        <code className="flex-1 px-3 py-2 bg-slate-900 text-emerald-300 text-xs font-mono rounded break-all">
          {value}
        </code>
        <Button size="sm" variant="outline" onClick={copy} className="h-9 text-xs shrink-0">
          {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
          <span className="ml-1">{copied ? "Copiado" : "Copiar"}</span>
        </Button>
      </div>
    </div>
  );
}

export default function Partners() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [docs, setDocs] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [reveal, setReveal] = useState(null); // {kind, value, partner}
  const [confirmDelete, setConfirmDelete] = useState(null);

  // create form
  const [form, setForm] = useState({ name: "", slug: "", webhook_url: "", hmac_required: false });
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: pData }, { data: dData }] = await Promise.all([
        api.get("/admin/partners"),
        api.get("/admin/docs"),
      ]);
      setItems(pData.items || []);
      setDocs(dData.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onCreate = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/admin/partners", form);
      setReveal({ kind: "api_key", value: data.api_key, partner: data.partner });
      setShowCreate(false);
      setForm({ name: "", slug: "", webhook_url: "", hmac_required: false });
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSubmitting(false); }
  };

  const onPatch = async (partnerId, patch) => {
    try {
      await api.patch(`/admin/partners/${partnerId}`, patch);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const onRotateApiKey = async (partner) => {
    if (!window.confirm(`¿Rotar la API key de ${partner.name}? La actual dejará de funcionar inmediatamente.`)) return;
    try {
      const { data } = await api.post(`/admin/partners/${partner.id}/rotate-api-key`);
      setReveal({ kind: "api_key", value: data.api_key, partner: data.partner });
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onRotateWebhookSecret = async (partner) => {
    if (!window.confirm(`¿Rotar el webhook_secret de ${partner.name}? El partner tendrá que actualizar su verificador.`)) return;
    try {
      const { data } = await api.post(`/admin/partners/${partner.id}/rotate-webhook-secret`);
      setReveal({ kind: "webhook_secret", value: data.webhook_secret, partner: data.partner });
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onRotateHmacSecret = async (partner) => {
    if (!window.confirm(`¿Rotar el hmac_secret entrante de ${partner.name}? Sus llamadas a /v1/* dejarán de validar hasta que actualicen.`)) return;
    try {
      const { data } = await api.post(`/admin/partners/${partner.id}/rotate-hmac-secret`);
      setReveal({ kind: "hmac_secret", value: data.hmac_secret, partner: data.partner });
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onDelete = async () => {
    try {
      await api.delete(`/admin/partners/${confirmDelete.id}`);
      toast.success(`Partner ${confirmDelete.name} eliminado`);
      setConfirmDelete(null);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadDoc = async (doc) => {
    try {
      const r = await api.get(`/admin/docs/${doc.filename}`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const docLabel = (fn) => {
    const isWebhook = fn.includes("webhook");
    const isEn = fn.includes(".en.");
    return `${isWebhook ? "Webhooks salientes" : "HMAC entrante"} · ${isEn ? "EN" : "ES"}`;
  };

  return (
    <Layout>
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2" style={{ fontFamily: "Chivo, sans-serif" }}>
            <Users className="w-6 h-6" /> Partners
          </h1>
          <p className="text-sm text-slate-600 mt-0.5">
            Tiendas autorizadas a llamar al bridge. Cada partner tiene su API key (autenticación),
            su <code className="font-mono text-[11px]">hmac_secret</code> (firma entrante en <code className="font-mono text-[11px]">/v1/*</code>)
            y su <code className="font-mono text-[11px]">webhook_secret</code> (firma saliente de webhooks).
          </p>
        </div>
        <Button
          className="h-9 bg-slate-900 hover:bg-slate-800 text-white text-xs"
          onClick={() => setShowCreate(true)}
          data-testid="create-partner-button"
        >
          <Plus className="w-3.5 h-3.5 mr-1.5" /> Alta de partner
        </Button>
      </div>

      {/* Integration docs for partners (PDFs ready to send) */}
      {docs.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-md p-4 mb-6" data-testid="partner-docs-section">
          <div className="flex items-center gap-2 mb-3">
            <FileDown className="w-4 h-4 text-slate-700" />
            <h2 className="text-xs font-bold tracking-wide uppercase">Documentación para enviar al partner</h2>
          </div>
          <p className="text-[11px] text-slate-500 mb-3">
            PDFs listos para entregar a los desarrolladores del partner. La nota técnica que envías junto con la API key
            y los secrets generados aquí.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {docs.map((d) => (
              <button
                key={d.filename}
                onClick={() => downloadDoc(d)}
                data-testid={`download-doc-${d.filename}`}
                className="flex items-center justify-between gap-3 px-3 py-2 border border-slate-200 rounded-md hover:bg-slate-50 text-left"
              >
                <div className="flex items-center gap-2">
                  <FileDown className="w-3.5 h-3.5 text-slate-500" />
                  <div>
                    <div className="text-xs font-semibold text-slate-900">{docLabel(d.filename)}</div>
                    <div className="text-[10px] font-mono text-slate-500">{d.filename} · {Math.round(d.size_bytes / 1024)} KB</div>
                  </div>
                </div>
                <span className="text-[10px] font-mono text-slate-400">PDF</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Partner</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Slug</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider font-bold text-slate-600">API key</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Webhook URL</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider font-bold text-slate-600">HMAC obligatorio</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Activo</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Creado</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider font-bold text-slate-600 text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={8} className="text-center text-sm text-slate-500 py-8" data-testid="partners-loading">Cargando…</TableCell></TableRow>
            )}
            {!loading && items.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center text-sm text-slate-500 py-8" data-testid="partners-empty">Sin partners. Crea el primero.</TableCell></TableRow>
            )}
            {!loading && items.map((p) => (
              <TableRow key={p.id} data-testid={`partner-row-${p.slug}`}>
                <TableCell className="text-sm font-semibold">{p.name}</TableCell>
                <TableCell className="text-xs font-mono text-slate-700">{p.slug}</TableCell>
                <TableCell className="text-xs font-mono text-slate-700">{p.api_key_prefix}</TableCell>
                <TableCell className="text-[11px] font-mono text-slate-700 max-w-[280px] truncate" title={p.webhook_url}>
                  {p.webhook_url || <span className="text-slate-400">—</span>}
                </TableCell>
                <TableCell>
                  <Switch
                    checked={!!p.hmac_required}
                    onCheckedChange={(v) => onPatch(p.id, { hmac_required: v })}
                    data-testid={`partner-hmac-toggle-${p.slug}`}
                  />
                </TableCell>
                <TableCell>
                  <Switch
                    checked={!!p.enabled}
                    onCheckedChange={(v) => onPatch(p.id, { enabled: v })}
                    data-testid={`partner-enabled-toggle-${p.slug}`}
                  />
                </TableCell>
                <TableCell className="text-[11px] font-mono text-slate-700">{fmtDate(p.created_at)}</TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex items-center gap-1">
                    <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => onRotateApiKey(p)} data-testid={`partner-rotate-key-${p.slug}`}>
                      <KeyRound className="w-3 h-3 mr-1" /> API
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => onRotateWebhookSecret(p)} data-testid={`partner-rotate-wh-${p.slug}`}>
                      <WebhookIcon className="w-3 h-3 mr-1" /> WH secret
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => onRotateHmacSecret(p)} data-testid={`partner-rotate-hmac-${p.slug}`}>
                      <ShieldCheck className="w-3 h-3 mr-1" /> HMAC
                    </Button>
                    {p.slug !== "evolvo" && (
                      <Button size="sm" variant="outline" className="h-7 text-[11px] text-red-700 hover:bg-red-50 border-red-200" onClick={() => setConfirmDelete(p)} data-testid={`partner-delete-${p.slug}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-md" data-testid="create-partner-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Chivo, sans-serif" }}>Alta de partner</DialogTitle>
            <DialogDescription className="text-xs">
              Generaremos automáticamente la API key, el <code className="font-mono">webhook_secret</code> y el
              <code className="font-mono"> hmac_secret</code>. La API key se mostrará UNA sola vez.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-semibold">Nombre</Label>
              <Input data-testid="partner-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="ej. Bici Madrid" />
            </div>
            <div>
              <Label className="text-xs font-semibold">Slug</Label>
              <Input data-testid="partner-slug-input" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase() })} placeholder="ej. bici-madrid" />
              <p className="text-[10px] text-slate-500 mt-1">Identificador estable. Sólo alfanumérico, guiones y subrayados.</p>
            </div>
            <div>
              <Label className="text-xs font-semibold">Webhook URL (opcional)</Label>
              <Input data-testid="partner-webhook-url-input" value={form.webhook_url} onChange={(e) => setForm({ ...form, webhook_url: e.target.value })} placeholder="https://..." />
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-xs font-semibold">HMAC entrante obligatorio</Label>
              <Switch checked={form.hmac_required} onCheckedChange={(v) => setForm({ ...form, hmac_required: v })} data-testid="partner-hmac-required-toggle" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)} className="text-xs">Cancelar</Button>
            <Button onClick={onCreate} disabled={submitting || !form.name || !form.slug} className="bg-slate-900 hover:bg-slate-800 text-white text-xs" data-testid="partner-create-submit">
              {submitting ? "Creando…" : "Crear partner"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reveal secret dialog */}
      <Dialog open={!!reveal} onOpenChange={(v) => !v && setReveal(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="reveal-secret-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "Chivo, sans-serif" }}>
              <AlertOctagon className="w-5 h-5 text-amber-600" />
              {reveal?.kind === "api_key" && "Nueva API key"}
              {reveal?.kind === "webhook_secret" && "Nuevo webhook_secret"}
              {reveal?.kind === "hmac_secret" && "Nuevo hmac_secret"}
            </DialogTitle>
            <DialogDescription className="text-xs">
              Guarda este valor ahora y compártelo con el partner por canal seguro. <strong>No volverá a mostrarse en claro.</strong>
            </DialogDescription>
          </DialogHeader>
          {reveal && (
            <div className="space-y-3">
              <div className="text-xs text-slate-600">
                Partner: <span className="font-mono font-semibold">{reveal.partner.name}</span> ({reveal.partner.slug})
              </div>
              <CopyBox
                label={reveal.kind === "api_key" ? "X-Partner-Api-Key" :
                       reveal.kind === "webhook_secret" ? "webhook_secret (firma saliente)" :
                       "hmac_secret (firma entrante /v1/*)"}
                value={reveal.value}
                testId="revealed-secret-box"
              />
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setReveal(null)} className="bg-slate-900 hover:bg-slate-800 text-white text-xs" data-testid="reveal-close">
              He copiado, cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm delete */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar partner {confirmDelete?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Sus API key y secrets dejarán de funcionar inmediatamente. Las transacciones históricas
              se mantienen. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={onDelete} className="bg-red-600 hover:bg-red-700" data-testid="partner-delete-confirm">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
